// Template207: Osaka - Shinsekai (Día 1, parte 2)
// Equivalente a Page7.jsx de guia-osaka. Datos desde page (backend).

import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

const Template207 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page?.texts?.[0]?.content;
	const titleOverlay = page?.texts?.[1]?.content;
	const intro = page?.texts?.[2]?.content;
	const block1Title = page?.texts?.[3]?.content;
	const block1Body = page?.texts?.[4]?.content;
	const boxText = page?.texts?.[5]?.content;
	const block2Title = page?.texts?.[6]?.content;
	const block2Body = page?.texts?.[7]?.content;
	const tipKushikatsu = page?.texts?.[8]?.content;
	const block3Title = page?.texts?.[9]?.content;
	const block3Body = page?.texts?.[10]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found?.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const portadaImage = getImageSrc("portada");
	const shitennoImage = getImageSrc("shitenno");
	const shinsekai2Image = getImageSrc("shinsekai2");
	const comiendoImage = getImageSrc("comiendo");
	const torreImage = getImageSrc("torre");
	const portadaSticker = getCanvasImageUrl("portada-sticker");
	const logoImage = getCanvasImageUrl("logo");

	const sabiasQue = Array.isArray(page?.data?.sabiasQue)
		? page.data.sabiasQue
		: [];

	const actualHeaderColor = headerBackgroundColor || "#1F4E3D";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{portadaImage && (
					<Box
						component="img"
						src={portadaImage}
						alt=""
						sx={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				)}
				{titleOverlay && (
					<Box
						component="h1"
						sx={{
							fontSize: isSmallScreen ? 28 : 32,
							textAlign: "center",
							position: "absolute",
							bottom: 0,
							margin: 1.25,
							left: 20,
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{titleOverlay}
					</Box>
				)}
				{portadaSticker && (
					<Box
						component="img"
						src={portadaSticker}
						alt=""
						sx={{ width: 90, position: "absolute", bottom: 60, left: 20 }}
					/>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
				}}
			>
				{intro && (
					<Box
						sx={{
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginBottom: 1.5,
							marginTop: "-10px",
						}}
					>
						{intro}
					</Box>
				)}

				{/* BLOQUE 1: Shitenno-ji */}
				<Box
					data-block-id="text-2"
					sx={{
						display: "flex",
						alignItems: "flex-start",
						gap: 2,
						position: "relative",
					}}
				>
					{shitennoImage && (
						<Box
							component="img"
							src={shitennoImage}
							alt=""
							sx={{
								width: "40%",
								height: 110,
								borderRadius: "12px",
								objectFit: "cover",
							}}
						/>
					)}
					<Box sx={{ width: "60%" }}>
						{block1Title && (
							<Box component="h3" sx={estiloH3}>
								{block1Title}
							</Box>
						)}
						{block1Body && (
							<Box component="p" sx={{ ...estiloP, color: contentColor }}>
								{block1Body}
							</Box>
						)}
					</Box>
				</Box>

				{/* Caja "Desde Shitennō-ji..." */}
				{boxText && (
					<Box
						data-block-id="text-3"
						sx={{
							backgroundColor: "#d1e6de",
							border: "2px solid #1F4E3D",
							borderRadius: "12px",
							padding: "8px 16px",
							fontSize: "13px",
							color: contentColor,
							marginTop: 1,
							textAlign: "center",
							marginBottom: isSmallScreen ? 1.25 : 0,
						}}
					>
						<Box component="p" sx={estiloP}>
							{boxText}
						</Box>
					</Box>
				)}

				{/* BLOQUE 2: Shinsekai + sabiasQue + tip kushikatsu + comiendo */}
				<Box
					data-block-id="text-4"
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
						marginTop: 1.25,
						marginBottom: isSmallScreen ? 1 : 0,
					}}
				>
					<Box sx={{ display: "flex", gap: 2, position: "relative" }}>
						{sabiasQue.length > 0 && (
							<>
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? "auto" : -40,
										top: isSmallScreen ? 0 : "auto",
										right: isSmallScreen ? "60%" : "20%",
									}}
								>
									<SabiasQue
										icono={sabiasQue[0]?.icono ?? "🏮"}
										texto={sabiasQue[0]?.texto ?? ""}
									/>
								</Box>
								{sabiasQue[1] && (
									<Box
										sx={{
											position: "absolute",
											bottom: isSmallScreen ? "auto" : 0,
											top: isSmallScreen ? 40 : "auto",
											right: isSmallScreen ? "60%" : "20%",
										}}
									>
										<SabiasQue
											icono={sabiasQue[1]?.icono ?? "🍢"}
											texto={sabiasQue[1]?.texto ?? ""}
										/>
									</Box>
								)}
								{sabiasQue[2] && (
									<Box
										sx={{
											position: "absolute",
											bottom: isSmallScreen ? -100 : 0,
											right: isSmallScreen ? "60%" : "27%",
										}}
									>
										<SabiasQue
											icono={sabiasQue[2]?.icono ?? "⚠️"}
											texto={sabiasQue[2]?.texto ?? ""}
										/>
									</Box>
								)}
							</>
						)}
						{shinsekai2Image && (
							<Box
								component="img"
								src={shinsekai2Image}
								alt=""
								sx={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{block2Title && (
								<Box component="h3" sx={estiloH3}>
									{block2Title}
								</Box>
							)}
							{block2Body && (
								<Box component="p" sx={estiloP}>
									{block2Body}
								</Box>
							)}
						</Box>
						{tipKushikatsu && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? -60 : -30,
									left: 50,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 16px 0px",
									padding: "10px 12px",
									fontSize: 11,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 300,
									zIndex: 6,
								}}
							>
								{tipKushikatsu}
							</Box>
						)}
						{comiendoImage && (
							<Box
								component="img"
								src={comiendoImage}
								alt=""
								sx={{
									width: 100,
									position: "absolute",
									bottom: -100,
									left: -20,
									zIndex: 5,
								}}
							/>
						)}
					</Box>

					{/* BLOQUE 3: Torre Tsutenkaku */}
					<Box
						sx={{ display: "flex", gap: 2, marginTop: isSmallScreen ? 8 : 3 }}
					>
						{torreImage && (
							<Box
								component="img"
								src={torreImage}
								alt=""
								sx={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{block3Title && (
								<Box component="h3" sx={estiloH3}>
									{block3Title}
								</Box>
							)}
							{block3Body && (
								<Box component="p" sx={estiloP}>
									{block3Body}
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template207.thumbnail = "/images/template-1.png";

export default Template207;
